import React, { DragEvent, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import cn from "classnames";
import { useTranslations } from "next-intl";
import { When } from "@/components/common/When";
import LoadingDots from "./LoadingDots/LoadingDots";
import Error from "@/components/common/Error/Error";
import CustomLogo from "./CustomLogo";
import useLogo from "@/hooks/common/useLogo";
import useLogoUpdate from "@/hooks/common/useLogoUpdate";
import useGenerateHomeQr from "@/components/page-modules/home-page/hooks/useGenerateHomeQr";
import useDeleteLogo from "@/hooks/common/useDeleteLogo";
import { scrollToLastItem } from "@/helper/helper";
import { useCustomLogo, useUserDesign } from "@/states/design.state";
import { useConfig, useGenerateResult } from "@/states/common.state";
import { useIsloading } from "@/states/ui.state";
import { FiUploadCloud } from "react-icons/fi";
import style from "./Logo.module.scss";
import { DASHBOARD_STEP_TYPES } from "@/components/page-modules/builder/__shared/data/type.repository";
import { useSolutionsSelect } from "@/states/solution.state";
interface GenericLogo {
  uuid: string;
  image: string;
  name: string;
  editable: boolean;
}
const Logo = () => {
  const isMobile = useMediaQuery({
    query: "(min-width: 1025px)"
  });
  const [error, setError] = useState<string>("");
  const [isDrag, setIsDrag] = useState<boolean>(false);
  const logosDivRef = useRef<HTMLDivElement>(null);
  const {
    logo,
    setLogo
  } = useUserDesign();
  const solutionsSelectedTypes = useSolutionsSelect(state => state.solutionsSelectedTypes);
  const {
    result
  } = useGenerateResult();
  const {
    config,
    payload,
    files
  } = useConfig();
  const {
    setIsloading
  } = useIsloading();
  const defaultSelectedLogo = config?.optionlogo;
  const {
    data,
    isLoading
  } = useLogo();
  const {
    customLogo,
    setCustomLogo
  } = useCustomLogo();
  const {
    mutateHomeQr,
    mutateLoading
  } = useGenerateHomeQr(false);
  const t = useTranslations("Logo");
  const mobileLogos = ["none", "scan-me-focused", "scan-me-simple", "scan-me-rounded", "scan-me-italic", "facebook", "instagram"];
  const selectedType = DASHBOARD_STEP_TYPES.find(c => c.qr_type?.toLowerCase() === solutionsSelectedTypes?.toString()?.toLowerCase());
  const {
    mutateLogoUpdate,
    isLoadingLogoUpdate,
    isUploadError
  } = useLogoUpdate({
    selectedType: selectedType!,
    setLogo,
    setError
  });
  const {
    isDeleteLoading
  } = useDeleteLogo(selectedType!);
  useEffect(() => {
    if (!isDeleteLoading && (!defaultSelectedLogo || logo !== defaultSelectedLogo)) {
      setLogo({
        upload_logo: defaultSelectedLogo === "none" ? "none" : defaultSelectedLogo
      });
    }
  }, [defaultSelectedLogo, isDeleteLoading]);
  useEffect(() => {
    if (!!config && !logo.upload_logo) {
      setLogo({
        upload_logo: "none"
      });
    }
  }, [logo.upload_logo]);
  useEffect(() => {
    if (customLogo.logo && logo.upload_logo === customLogo.logo.uuid) {
      scrollToLastItem(logosDivRef, "nearest");
    }
  }, [isLoadingLogoUpdate]);
  useEffect(() => {
    if (customLogo.logo && isUploadError) {
      setCustomLogo({
        logo: null,
        name: ""
      });
    }
  }, [isUploadError]);
  function handleLogoClick(uuid: string) {
    setIsloading(true);
    setLogo({
      upload_logo: uuid
    });
    if (!result || !mutateLoading) {
      mutateHomeQr({
        params: {
          ...payload,
          files
        },
        configParams: {
          optionlogo: uuid
        }
      });
    }
  }
  const getFilteredLogos = (logos: GenericLogo[] = [], isMobile: boolean): GenericLogo[] => {
    if (isMobile) return logos;
    const uniqueLogos = new Map(logos.filter(logo => mobileLogos.includes(logo.uuid) || logo.editable).map(logo => [logo.uuid, logo]));
    return Array.from(uniqueLogos.values());
  };

  // Usage
  const filteredLogos = getFilteredLogos(data?.data || [], isMobile);
  const handleFileUpload = (file: File) => {
    const size = file.size / (1024 * 1024);
    if (size > 2.1) {
      setError(t("fileSizeError"));
      setCustomLogo({
        logo: null,
        name: ""
      });
      return;
    }
    const formData = new FormData();
    formData.append("image", file);
    setCustomLogo({
      name: file.name
    });
    mutateLogoUpdate(formData);
    setIsDrag(false);
  };
  const handleUploadCustomLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      files
    } = e.target;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const {
      files
    } = e.dataTransfer;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
    setIsDrag(false);
  };
  const handleDragEnter = () => setIsDrag(true);
  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) return;
    setIsDrag(false);
  };
  return <div className={cn(style.wrapper, {
    [style.wrapper_fluid]: !isMobile
  })} data-sentry-component="Logo" data-sentry-source-file="Logo.tsx">
      <div className={style.flex} ref={logosDivRef}>
        <When condition={!isLoading} data-sentry-element="When" data-sentry-source-file="Logo.tsx">
          {filteredLogos?.map(({
          uuid,
          image,
          name
        }, index) => <button disabled={logo.upload_logo === uuid} className={cn(style.item, {
          [style.active]: logo.upload_logo === uuid,
          [style.borderless]: uuid === "none"
        })} key={index.toString() + uuid} onClick={() => handleLogoClick(uuid)}>
              <img src={image} alt={name} />
            </button>)}
        </When>
      </div>
      {!customLogo.logo ? <div className={cn(style.uploadBtn, {
      [style.centered]: isLoadingLogoUpdate,
      [style.bgColor]: isDrag
    })} onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDrop={handleDrop}>
          {!isLoadingLogoUpdate ? <div className={style.inputBox}>
              <label htmlFor="logo">
                <FiUploadCloud size={isMobile ? 17 : 16} color="#399930" />
                <div className={style.labelText_wrap}>
                  {isDrag ? <span className={style.dropHere}>{t("dropHere")}</span> : <>
                      <span>{!isMobile ? t("uploadLogo") : t("dragAndDrop")}</span>
                      <div className={style.formatBox}>
                        {isUploadError || error ? <Error mt={0} className={style.error_message}>
                            {error}
                          </Error> : <span className={style.infoText}>
                            {!isMobile ? "(JPG, JPEG, or PNG | 2MB max)" : t("supportedFileFormat")}
                          </span>}
                      </div>
                    </>}
                </div>
              </label>
              <input type="file" name="logo" id="logo" onChange={handleUploadCustomLogo} accept=".png,.svg,.jpg,.jpeg" />
            </div> : <LoadingDots />}
        </div> : <CustomLogo handleUploadCustomLogo={handleUploadCustomLogo} setError={setError} selectedType={selectedType!} />}
    </div>;
};
export default Logo;